/**
 * SellerOrderService — HTTP + local draft persistence for seller/admin orders
 * (AFV Online). Completely isolated from CartService / customer order flow.
 *
 * Draft: a single localStorage slot per user. Each save overwrites it.
 * Submit: POST afv/orders (same contract AFV uses).
 *
 * Future backend endpoints:
 *   GET/POST/PUT/DELETE afv/orders/drafts[/:id]
 *   GET afv/orders/last
 */
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { LocalStorageService } from '@services/local-storage.service';
import { AuthFacade } from '@facades/auth.facade';

import {
	ESellerOrderStatus,
	type ISellerOrder,
	type ISellerOrderDraft,
	type ISellerOrderFiscalSubmit,
	type ISellerOrderItem,
} from '@interfaces/seller-order';

const DRAFT_KEY_PREFIX = 'seller-order-drafts';
const DRAFT_ID = 1;

@Injectable({
	providedIn: 'root',
})
export class SellerOrderService {
	constructor(
		private readonly _http: HttpClient,
		private readonly localStorage: LocalStorageService,
		private readonly authFacade: AuthFacade
	) {}

	private get draftKey(): string {
		const userId = this.authFacade.authStateStorage?.user?.id ?? 'anon';
		return `${DRAFT_KEY_PREFIX}:${userId}`;
	}

	/** One draft per user. Older map-shaped saves collapse to the latest order. */
	private readDraft(): ISellerOrder | null {
		const raw = this.localStorage.get<unknown>(this.draftKey);
		if (!raw || typeof raw !== 'object') return null;

		const record = raw as Record<string, unknown>;
		if (Array.isArray(record['items'])) {
			return { ...(record as unknown as ISellerOrder), id: DRAFT_ID };
		}

		const orders = Object.values(record).filter(
			(entry): entry is ISellerOrder =>
				!!entry &&
				typeof entry === 'object' &&
				Array.isArray((entry as ISellerOrder).items)
		);
		if (!orders.length) return null;

		orders.sort(
			(a, b) =>
				new Date(b.updatedAt || 0).getTime() -
				new Date(a.updatedAt || 0).getTime()
		);
		const latest = { ...orders[0], id: DRAFT_ID };
		this.writeDraft(latest);
		return latest;
	}

	private writeDraft(order: ISellerOrder) {
		this.localStorage.set(this.draftKey, order);
	}

	private toDraftSummary(order: ISellerOrder): ISellerOrderDraft {
		return {
			id: DRAFT_ID,
			clientName: order.client?.name,
			total: order.total ?? 0,
			itemCount: order.items?.length ?? 0,
			updatedAt: order.updatedAt ?? new Date(),
		};
	}

	getDrafts(): Observable<ISellerOrderDraft[]> {
		const draft = this.readDraft();
		if (!draft || (!draft.clientId && !draft.items?.length)) return of([]);
		return of([this.toDraftSummary(draft)]);
	}

	getDraft(): Observable<ISellerOrder | null> {
		return of(this.readDraft());
	}

	/** Always overwrites the single draft. An empty order clears it. */
	saveDraft(order: Partial<ISellerOrder>): Observable<ISellerOrder> {
		const saved: ISellerOrder = {
			items: [],
			shipment: {},
			payment: {},
			subtotal: 0,
			totalDiscount: 0,
			total: 0,
			...order,
			id: DRAFT_ID,
			status: ESellerOrderStatus.DRAFT,
			updatedAt: new Date(),
			createdAt: order.createdAt ?? new Date(),
		};

		if (!saved.clientId && !saved.items.length) {
			this.localStorage.remove(this.draftKey);
			return of(saved);
		}

		this.writeDraft(saved);
		return of(saved);
	}

	deleteDraft(): Observable<void> {
		this.localStorage.remove(this.draftKey);
		return of(void 0);
	}

	/** Submit order via existing AFV endpoint. Clears the local draft. */
	submitOrder(
		order: ISellerOrder,
		fiscal?: ISellerOrderFiscalSubmit
	): Observable<ISellerOrder> {
		const payload = this.toAfvPayload(order, fiscal);
		return this._http.post<ISellerOrder>('afv/orders', payload).pipe(
			tap(() => this.localStorage.remove(this.draftKey)),
			catchError(err => throwError(() => err))
		);
	}

	getLastOrder(): Observable<ISellerOrder | null> {
		return of(this.readDraft());
	}

	private toAfvPayload(
		order: ISellerOrder,
		fiscal?: ISellerOrderFiscalSubmit
	): Record<string, unknown> {
		return {
			isAfv: true,
			clientId: order.clientId,
			addressId: order.shipment?.addressId ?? order.shipment?.address?.id,
			shippingMethodId: order.shipment?.shippingMethodId,
			shippingTypeId: order.shipment?.shippingTypeId,
			paymentMethodId: order.payment?.paymentMethodId,
			paymentTermId: order.payment?.paymentTermId,
			orderTypeId: order.payment?.orderTypeId,
			priceListId: order.priceListId,
			total: order.total,
			discount: order.totalDiscount,
			obs: order.shipment?.obs ?? order.obs,
			shipmentPrice: (order.items || []).reduce(
				(sum, item) => sum + (item.shipmentPrice || 0),
				0
			),
			...(order.isQuote && order.expiresAt
				? { expiresAt: order.expiresAt }
				: {}),
			...(fiscal?.sellingOperationId != null
				? { sellingOperationId: fiscal.sellingOperationId }
				: {}),
			...((order.storeId ?? fiscal?.storeId) != null
				? { storeId: order.storeId ?? fiscal?.storeId }
				: {}),
			...(order.storehouseId != null
				? { storehouseId: order.storehouseId }
				: {}),
			items: (order.items || []).map(item => {
				const storeId = item.storeId ?? order.storeId;
				const storehouseId = item.storehouseId ?? order.storehouseId;
				return {
					productId: item.productId,
					quantity: item.quantity,
					unitPrice: item.unitPrice,
					originalPrice: item.originalPrice,
					minimumPrice: item.minimumPrice,
					discountPercentage: item.discountPercentage,
					shipmentPrice: item.shipmentPrice,
					flexBalance: item.flexBalance,
					priceListId: item.priceListId ?? order.priceListId,
					...(storeId != null ? { storeId } : {}),
					...(storehouseId != null ? { storehouseId } : {}),
					packingId: item.packingId,
					...(fiscal ? this.itemTaxFields(item) : {}),
				};
			}),
		};
	}

	private itemTaxFields(item: ISellerOrderItem): Record<string, unknown> {
		const fields: Record<string, unknown> = {};
		const entries: Array<[string, unknown]> = [
			['icmsAmount', item.icmsAmount],
			['icmsRate', item.icmsRate],
			['icmsStAmount', item.icmsStAmount],
			['ipiAmount', item.ipiAmount],
			['ipiRate', item.ipiRate],
			['cbsAmount', item.cbsAmount],
			['cbsRate', item.cbsRate],
			['ibsAmount', item.ibsAmount],
			['ibsRate', item.ibsRate],
			['taxDetailsJson', item.taxDetailsJson],
		];
		for (const [key, value] of entries) {
			if (value != null) fields[key] = value;
		}
		return fields;
	}
}
